import React from "react";
import { observer } from "mobx-react";
import { Grid, Box } from "@mui/material";
import { useRootStore } from "../../../../store/contexts";
import { GroupContainer } from "../../../../common/components";
import { FormToolbox } from "../../../../common/controls/forms/FormToolbox";
import { FormSaveFooter } from "../../../../common/controls/forms/FormSaveFooter";

import FormExtras from "../../../../common/components/formExtras";
import { DownloadIcon } from "../../../../icons";
import { VisibilityIcon } from "../../../../icons";
import { PDFViewerDialog } from "../../../../common/components/PDFViewerDialog";
import { isOriginalCrudStatus, RowCrudStatus } from "../../../common/types";
import { TenantDocumentUpdateStore } from "../../store";
import { InputString } from "../../../common/controls";
import { InputText } from "../../../common/controls";
import { FileCRUDType } from "../../../common/types";
import { GridChangeSet } from "../../../common/types";
import { LoadFilesCRUDCompact } from "../../../common/controls";
import { ChunkingStrategy } from "../../../../api/graphql";
import { InputReference } from "../../../common/controls";
import { InputNumber } from "../../../common/controls";
import { FormControl } from "@mui/material";

export type TenantDocumentUpdateFormProps = {
  onSave?: () => void;
  onCancel?: () => void;
  /** Mientras se guarda, Guardar queda deshabilitado. */
  saving?: boolean;
  showToolbox?: boolean;
  showSaveFooter?: boolean;
  name: string;
  nameSelectedValue?: { displayId: string; id: string } | null;
  onNameChange?: (value: string) => void;
  nameError?: string;
  description: string;
  descriptionSelectedValue?: { displayId: string; id: string } | null;
  onDescriptionChange?: (value: string) => void;
  descriptionError?: string;
  fileOnRestoreFile: (id: string) => void;
  fileOnLoadFile: (file: any) => void;
  fileOnCommitFile: (changes: GridChangeSet) => void;
  fileFiles: FileCRUDType[];
  fileError?: string;
  processingError: string;
  processingErrorSelectedValue?: { displayId: string; id: string } | null;
  onProcessingErrorChange?: (value: string) => void;
  processingErrorError?: string;
  contentType: string;
  contentTypeSelectedValue?: { displayId: string; id: string } | null;
  onContentTypeChange?: (value: string) => void;
  contentTypeError?: string;
  chunkingStrategyOptions: { id: string; displayId: string }[];
  chunkingStrategyError?: string;
  chunkingStrategy: ChunkingStrategy;
  onChunkingStrategyChange?: (value: ChunkingStrategy) => void;
  chunkingSeparatorRegex: string;
  chunkingSeparatorRegexSelectedValue?: {
    displayId: string;
    id: string;
  } | null;
  onChunkingSeparatorRegexChange?: (value: string) => void;
  chunkingSeparatorRegexError?: string;
  chunkingMaxTokens: number | undefined;
  chunkingMaxTokensSelectedValue?: { displayId: string; id: string } | null;
  onChunkingMaxTokensChange?: (value: number | undefined) => void;
  chunkingMaxTokensError?: string;
  chunkingOverlapTokens: number | undefined;
  chunkingOverlapTokensSelectedValue?: { displayId: string; id: string } | null;
  onChunkingOverlapTokensChange?: (value: number | undefined) => void;
  chunkingOverlapTokensError?: string;
  dummyCRUDRestoredObservable: number;
  tenantDocumentUpdateStore: TenantDocumentUpdateStore;
};

export const TenantDocumentUpdateForm: React.FC<TenantDocumentUpdateFormProps> =
  observer(
    ({
      onSave = () => {},
      onCancel = () => {},
      saving = false,
      showToolbox = true,
      showSaveFooter,
      name = "",
      nameError,
      onNameChange = () => {},
      description = "",
      descriptionError,
      onDescriptionChange = () => {},
      fileOnRestoreFile,
      fileOnLoadFile,
      fileOnCommitFile,
      fileFiles,
      fileError,
      processingError = "",
      processingErrorError,
      onProcessingErrorChange = () => {},
      contentType = "",
      contentTypeError,
      onContentTypeChange = () => {},
      chunkingStrategyOptions = [],
      chunkingStrategyError,
      chunkingStrategy = "" as ChunkingStrategy,
      onChunkingStrategyChange = () => {},
      chunkingSeparatorRegex = "",
      chunkingSeparatorRegexError,
      onChunkingSeparatorRegexChange = () => {},
      chunkingMaxTokens = undefined,
      chunkingMaxTokensError,
      onChunkingMaxTokensChange = () => {},
      chunkingOverlapTokens = undefined,
      chunkingOverlapTokensError,
      onChunkingOverlapTokensChange = () => {},
      dummyCRUDRestoredObservable,
      tenantDocumentUpdateStore,
    }) => {
      const rootStore = useRootStore();
      const { langStore } = rootStore;

      const getViewAction = (
        onDownload: (props: {
          id: string;
          type: string;
          name: string;
          __status__: RowCrudStatus;
        }) => void,
        onViewPdf?: (props: any) => void
      ) => {
        const actions: any[] = [];
        if (onViewPdf) {
          actions.push({
            columnName: "__action_view_pdf",
            onClick: (props: any) =>
              isOriginalCrudStatus(props.__status__) ? onViewPdf(props) : {},
            icon: <VisibilityIcon />,
            label: langStore.viewCommand,
            isActionEnabled: (props: any) =>
              props.name?.toLowerCase().endsWith(".pdf"),
          });
        }
        actions.push({
          columnName: "__action_view",
          onClick: (props: any) =>
            isOriginalCrudStatus(props.__status__) ? onDownload(props) : {},
          icon: <DownloadIcon />,
          label: langStore.downloadCommand,
        });
        return actions;
      };
      return (
        <Grid>
          {showToolbox && (
            <FormToolbox
              saveAndCloseLabel={langStore.saveEntity}
              closeLabel={langStore.cancelUpdatedEntity}
              saveForm={() => onSave()}
              saveDisabled={saving}
              closeForm={() => onCancel()}
            />
          )}
          <Box>
            <FormExtras
              entity="TenantDocument"
              mode="update"
              values={{}}
              options={{}}
              onPatch={(campo: string, valor: any) => {
                switch (campo) {
                  case "name":
                    onNameChange?.(valor);
                    break;
                  case "description":
                    onDescriptionChange?.(valor);
                    break;
                  case "processingError":
                    onProcessingErrorChange?.(valor);
                    break;
                  case "contentType":
                    onContentTypeChange?.(valor);
                    break;
                  case "chunkingStrategy":
                    onChunkingStrategyChange?.(valor);
                    break;
                  case "chunkingSeparatorRegex":
                    onChunkingSeparatorRegexChange?.(valor);
                    break;
                  case "chunkingMaxTokens":
                    onChunkingMaxTokensChange?.(valor);
                    break;
                  case "chunkingOverlapTokens":
                    onChunkingOverlapTokensChange?.(valor);
                    break;
                }
              }}
            />
            <GroupContainer
              id="null"
              errorText={tenantDocumentUpdateStore.getCardErrors("null")}
              title={langStore.groupTenantDocumentLabel}
              collapsible={false}
            >
              <FormControl fullWidth>
                <InputString
                  autoFocus
                  value={name}
                  error={Boolean(nameError)}
                  warning={
                    !nameError &&
                    Boolean(tenantDocumentUpdateStore.warnings["name"])
                  }
                  helperText={
                    nameError ||
                    tenantDocumentUpdateStore.warnings["name"]?.message
                  }
                  label={langStore.TenantDocumentNameLabel}
                  onChange={(value: string) => onNameChange(value)}
                  description={langStore.TenantDocumentNameDescription}
                />
                <InputText
                  value={description}
                  error={Boolean(descriptionError)}
                  warning={
                    !descriptionError &&
                    Boolean(tenantDocumentUpdateStore.warnings["description"])
                  }
                  helperText={
                    descriptionError ||
                    tenantDocumentUpdateStore.warnings["description"]?.message
                  }
                  label={langStore.TenantDocumentDescriptionLabel}
                  onChange={(value: string) => onDescriptionChange(value)}
                  nullText={langStore.optionalFieldHint}
                  description={langStore.TenantDocumentDescriptionDescription}
                />
                <LoadFilesCRUDCompact
                  rows={fileFiles}
                  maxRows={1}
                  menuActions={getViewAction(
                    (props) =>
                      tenantDocumentUpdateStore.fileDownload(props, "file"),
                    (props) =>
                      tenantDocumentUpdateStore.viewFilePdf(props, "file")
                  )}
                  showAddCommand={true}
                  showDeleteCommand={true}
                  label={langStore.TenantDocumentFileLabel}
                  description={langStore.TenantDocumentFileDescription}
                  onLoadFile={(file: File) => fileOnLoadFile(file)}
                  columns={[
                    { name: "mimetype", title: langStore.fileTypeLabel },
                    { name: "name", title: langStore.fileNameLabel },
                    { name: "formattedSize", title: langStore.fileSizeLabel },
                  ]}
                  onRestore={(id: string) => fileOnRestoreFile(id)}
                  onCommitChanges={(changes: GridChangeSet) =>
                    fileOnCommitFile(changes)
                  }
                  dummyCRUDRestoredObservable={dummyCRUDRestoredObservable}
                  error={fileError}
                />
                <InputString
                  value={processingError}
                  error={Boolean(processingErrorError)}
                  warning={
                    !processingErrorError &&
                    Boolean(
                      tenantDocumentUpdateStore.warnings["processingError"]
                    )
                  }
                  helperText={
                    processingErrorError ||
                    tenantDocumentUpdateStore.warnings["processingError"]
                      ?.message
                  }
                  label={langStore.TenantDocumentProcessingErrorLabel}
                  onChange={(value: string) => onProcessingErrorChange(value)}
                  nullText={langStore.optionalFieldHint}
                  description={
                    langStore.TenantDocumentProcessingErrorDescription
                  }
                />
                <InputString
                  value={contentType}
                  error={Boolean(contentTypeError)}
                  warning={
                    !contentTypeError &&
                    Boolean(tenantDocumentUpdateStore.warnings["contentType"])
                  }
                  helperText={
                    contentTypeError ||
                    tenantDocumentUpdateStore.warnings["contentType"]?.message
                  }
                  label={langStore.TenantDocumentContentTypeLabel}
                  onChange={(value: string) => onContentTypeChange(value)}
                  nullText={langStore.optionalFieldHint}
                  description={langStore.TenantDocumentContentTypeDescription}
                />
                <InputReference
                  id="chunkingStrategy"
                  disabled={false}
                  error={Boolean(chunkingStrategyError)}
                  helperText={
                    chunkingStrategyError ||
                    tenantDocumentUpdateStore.warnings["chunkingStrategy"]
                      ?.message
                  }
                  label={langStore.TenantDocumentChunkingStrategyLabel}
                  options={chunkingStrategyOptions}
                  selectedValue={
                    chunkingStrategy
                      ? {
                          id: chunkingStrategy,
                          displayId:
                            chunkingStrategyOptions.find(
                              (o: { id: string }) => o.id === chunkingStrategy
                            )?.displayId || chunkingStrategy,
                        }
                      : null
                  }
                  onChange={() => {}}
                  onSelect={(value: { id: string; displayId: string } | null) =>
                    onChunkingStrategyChange(
                      (value?.id as ChunkingStrategy) ||
                        ("" as ChunkingStrategy)
                    )
                  }
                />
                <InputString
                  value={chunkingSeparatorRegex}
                  error={Boolean(chunkingSeparatorRegexError)}
                  warning={
                    !chunkingSeparatorRegexError &&
                    Boolean(
                      tenantDocumentUpdateStore.warnings[
                        "chunkingSeparatorRegex"
                      ]
                    )
                  }
                  helperText={
                    chunkingSeparatorRegexError ||
                    tenantDocumentUpdateStore.warnings["chunkingSeparatorRegex"]
                      ?.message
                  }
                  label={langStore.TenantDocumentChunkingSeparatorRegexLabel}
                  onChange={(value: string) =>
                    onChunkingSeparatorRegexChange(value)
                  }
                  nullText={langStore.optionalFieldHint}
                  description={
                    langStore.TenantDocumentChunkingSeparatorRegexDescription
                  }
                />
                <InputNumber
                  value={chunkingMaxTokens}
                  nullable={true}
                  error={Boolean(chunkingMaxTokensError)}
                  warning={
                    !chunkingMaxTokensError &&
                    Boolean(
                      tenantDocumentUpdateStore.warnings["chunkingMaxTokens"]
                    )
                  }
                  helperText={
                    chunkingMaxTokensError ||
                    tenantDocumentUpdateStore.warnings["chunkingMaxTokens"]
                      ?.message
                  }
                  label={langStore.TenantDocumentChunkingMaxTokensLabel}
                  decimalScale={0}
                  nullText={langStore.optionalFieldHint}
                  description={
                    langStore.TenantDocumentChunkingMaxTokensDescription
                  }
                  onChange={(value: number | undefined) =>
                    onChunkingMaxTokensChange(value)
                  }
                />
                <InputNumber
                  value={chunkingOverlapTokens}
                  nullable={true}
                  error={Boolean(chunkingOverlapTokensError)}
                  warning={
                    !chunkingOverlapTokensError &&
                    Boolean(
                      tenantDocumentUpdateStore.warnings[
                        "chunkingOverlapTokens"
                      ]
                    )
                  }
                  helperText={
                    chunkingOverlapTokensError ||
                    tenantDocumentUpdateStore.warnings["chunkingOverlapTokens"]
                      ?.message
                  }
                  label={langStore.TenantDocumentChunkingOverlapTokensLabel}
                  decimalScale={0}
                  nullText={langStore.optionalFieldHint}
                  description={
                    langStore.TenantDocumentChunkingOverlapTokensDescription
                  }
                  onChange={(value: number | undefined) =>
                    onChunkingOverlapTokensChange(value)
                  }
                />
              </FormControl>
            </GroupContainer>
          </Box>
          {(showSaveFooter ?? !showToolbox) && (
            <FormSaveFooter
              onSave={() => onSave()}
              label={langStore.saveEntity}
              disabled={saving}
            />
          )}
          <PDFViewerDialog
            open={tenantDocumentUpdateStore.pdfViewerOpen}
            onClose={() => tenantDocumentUpdateStore.closePdfViewer()}
            pdfUrl={tenantDocumentUpdateStore.pdfViewerUrl}
            title={tenantDocumentUpdateStore.pdfViewerTitle}
          />
        </Grid>
      );
    }
  );
